import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
    useMsal,
    useAccount,
    useIsAuthenticated,
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { Config } from 'config';
import { formatDisplayName } from 'mydnv/utils/format-display-name';
import { getAccessToken } from 'mydnv/utils/access-token';
import * as signInService from 'mydnv/services/sign-in-service';

export const MyDNVContext = React.createContext();

export const useMyDNVContext = () => useContext(MyDNVContext);

const apiConfig = {
    apiUrl: `${Config.MyDNV.azureFnPath}/getMyDNVBookmarks`, // URL of your protected API
};
const SignInRequest = Config.MyDNV.loginRequest;
const SignUpRequest = Config.MyDNV.signUpRequest;
const LoginRequest = Config.MyDNV.loginRequest;
const InteractionStatusNone = InteractionStatus.None;
const storeKeyVerifyingEmailAddress = 'verifying-email-address';
const storeKeySignOutSignUpTimestamp = 'sign-out-sign-up';
const ParseVerifyEmailRequestUrl = () => {
    let stringFormatRequest = null;

    if (window.location.search.length > 0) {
        const params = new URLSearchParams(window.location.search);
        const verifyToken = params.get('verify');
        if (!!verifyToken) {
            const verifyEmailRequestObject = {
                verify: verifyToken,
            };
            stringFormatRequest = JSON.stringify(verifyEmailRequestObject);
            localStorage.setItem(storeKeyVerifyingEmailAddress, stringFormatRequest);
        }
    }

    if (null === stringFormatRequest) {
        stringFormatRequest = localStorage.getItem(storeKeyVerifyingEmailAddress);
    }

    return stringFormatRequest;
};
const verifyEmailRequest = ParseVerifyEmailRequestUrl();
const signOutSignUpTimestamp = localStorage.getItem(storeKeySignOutSignUpTimestamp);

export const MyDNVProvider = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0] || {});
    const isAuthenticated = useIsAuthenticated();

    const [bookmarks, setBookmark] = useState(undefined);
    const [bmTest, setBMTest] = useState(undefined);
    const [displayName, setDisplayName] = useState('');
    const [emailVerified, setEmailVerified] = useState(undefined);
    const [userFirstName, setUserFirstName] = useState('');
    const [email, setEmail] = useState('');
    const [displayNameLoaded, setDisplayNameLoaded] = useState(false);
    const [showBookmarksTip, setShowBookmarksTip] = useState(false);
    const [showVerifiedTip, setShowVerifiedTip] = useState(false);
    const [showVerifySpinner, setShowVerifySpinner] = useState(false);
    const [showVerifySent, setShowVerifySent] = useState(false);
    const [userAccount, setUserAccount] = useState(null);

    // sign-in component related
    const [isCheckYourEmailModalOpen, setIsCheckYourEmailModalOpen] = useState(false);
    const [requestVerificationEmailActionType, setRequestVerificationEmailActionType] = useState(null);
    const [emailVerificationApiCalled, setEmailVerificationApiCalled] = useState(false);
    const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] = useState(false);
    const [emailVerificationModalMessage, setEmailVerificationModalMessage] = useState('');
    const [verifyEmailRequestBody, setVerifyEmailRequestBody] = useState(null);

    const request = useMemo(() => {
        return {
            ...LoginRequest,
            account: account,
        };
    }, [account]);

    const handleBookmarks = useCallback((args) => {
        fetch(apiConfig.apiUrl, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${args.token}`,
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                response
                    .json()
                    .then((json) => {
                        setBookmark(JSON.stringify(json));
                        setBMTest(json);
                    })
                    .catch((e) => console.error(e));
            })
            .catch((e) => console.error(e));
    }, []);

    useEffect(() => {
        if (!isAuthenticated && inProgress === InteractionStatusNone && !!signOutSignUpTimestamp) {
            localStorage.removeItem(storeKeySignOutSignUpTimestamp);
            instance.loginRedirect(SignUpRequest).catch((e) => {
                console.error(e);
            });
        }
    }, [isAuthenticated, inProgress]);

    useEffect(() => {
        if (!isAuthenticated && inProgress === InteractionStatusNone && !!verifyEmailRequest) {
            instance.loginRedirect(LoginRequest).catch((e) => {
                console.error(e);
            });
        }
    }, [isAuthenticated, inProgress]);

    useEffect(() => {
        if (isAuthenticated && account !== null && userAccount === null && inProgress === InteractionStatusNone) {
            const handleUserIdentityResponse = (response) => {
                if (401 === response.status) {
                    instance.logoutRedirect({
                        postLogoutRedirectUri: '/',
                    });
                }

                setUserAccount(response.data);

                const userInfo = response.data;
                setDisplayName(formatDisplayName(userInfo.first_Name, userInfo.last_Name));
                setUserFirstName(userInfo.first_Name);
                if (userInfo.emails.length > 0) {
                    setEmail(userInfo.emails[0]);
                }
                setDisplayNameLoaded(true);
                if (200 === response.status) {
                    setEmailVerified(true);
                    setEmailVerificationApiCalled(true);
                } else if (403 === response.status) {
                    if (!!verifyEmailRequest) {
                        setIsEmailVerificationModalOpen(true);
                        setEmailVerificationModalMessage('waiting');
                        setVerifyEmailRequestBody(JSON.parse(verifyEmailRequest));
                    } else {
                        setEmailVerified(false);
                        setIsCheckYourEmailModalOpen(true);
                        setRequestVerificationEmailActionType('page-load');
                    }
                }
            };

            const handleUserIdentity = (args) => {
                signInService
                    .getUserIdentity(args)
                    .then((response) => {
                        handleUserIdentityResponse(response);
                    })
                    .catch((e) => console.error(e));
            };

            getAccessToken(request, instance, handleUserIdentity, null, null);
        }
    }, [isAuthenticated, account, userAccount, inProgress, request, instance]);

    useEffect(() => {
        if (
            isAuthenticated &&
            emailVerified &&
            account !== null &&
            !bookmarks &&
            inProgress === InteractionStatusNone &&
            !signOutSignUpTimestamp
        ) {
            getAccessToken(request, instance, handleBookmarks, null, null);
        }
    }, [isAuthenticated, emailVerified, account, bookmarks, inProgress, request, instance, handleBookmarks]);

    const value = {
        InteractionStatusNone,
        bookmarks,
        setBookmark,
        bmTest,
        setBMTest,
        displayName,
        setDisplayName,
        emailVerified,
        setEmailVerified,
        userFirstName,
        setUserFirstName,

        email,
        setEmail,
        displayNameLoaded,
        setDisplayNameLoaded,

        showBookmarksTip,
        setShowBookmarksTip,
        showVerifiedTip,
        setShowVerifiedTip,
        showVerifySpinner,
        setShowVerifySpinner,
        showVerifySent,
        setShowVerifySent,
        account,
        isAuthenticated,
        signOutSignUpTimestamp,

        storeKeyVerifyingEmailAddress,
        storeKeySignOutSignUpTimestamp,

        AuthenticatedTemplate,
        UnauthenticatedTemplate,
        instance,
        accounts,
        inProgress,
        userAccount,
        setUserAccount,
        isCheckYourEmailModalOpen,
        requestVerificationEmailActionType,
        setRequestVerificationEmailActionType,
        emailVerificationApiCalled,
        setEmailVerificationApiCalled,
        isEmailVerificationModalOpen,
        setIsEmailVerificationModalOpen,
        emailVerificationModalMessage,
        setEmailVerificationModalMessage,
        verifyEmailRequestBody,
        setVerifyEmailRequestBody,
        SignInRequest,
        SignUpRequest,
        LoginRequest,
    };

    if (
        inProgress === InteractionStatusNone &&
        (!isAuthenticated || emailVerified === false) &&
        /\/mydnv/i.test(window.location.pathname)
    ) {
        window.location.href = '/'; // can't navigate here because outside of router
        return <MyDNVContext.Provider value={value}></MyDNVContext.Provider>;
    }

    return <MyDNVContext.Provider value={value}>{children}</MyDNVContext.Provider>;
};
